import SpecialtyGroupingsTable from '../tables/SpecialtyGroupingsTable';
import ReportsTable from '../tables/ReportsTable';
import {useContext, useMemo} from 'react';
import tableHeaders from '../tables/table-headers';
import {IInitialData, ISpecialtyGroupingsData, IMaxTimeDistTableRow, IMaxTimeDistData} from '../../types';
import MapContainer from '../MapContainer';
import {createDesignationFillPaint, DESIGNATION_COLORS} from '../boundary-layer-strategies';
import MapLegend from '../MapLegend';
import PageBreak from '../PageBreak';
import PageWithFootnotes from '../PageWithFootnotes';
import {NetworkContext} from '../../../../lib/context';


interface IIntroductionProps {
    specialtyGroupings: IInitialData<ISpecialtyGroupingsData>;
    maxTimeDistData: IInitialData<IMaxTimeDistData>;
    standardType: string;
    standardVersion: string;
}

const Introduction = (props: IIntroductionProps) => {

    const noStore = useContext(NetworkContext);

    const states = useMemo(() => noStore.getGeoFilterUniqueValuesByKey(
        'GEOGRAPHY',
        'state_additional_info.state'
    ), [noStore.availableGeographyFilters]);
    
    const {
        specialtyGroupings, 
        maxTimeDistData: _maxTimeDistData,
        standardType,
        standardVersion
    } = props;
    const {providerSpecialties = [], facilitySpecialties = []} = specialtyGroupings.data || {};
    const maxTimeDistData = _maxTimeDistData.data || [];

    // Get the year from standard version (e.g., "2026.1" -> "2026")
    const year = standardVersion.split('.')[0];

    // Determine regulatory standard display name and URLs based on standardType
    const isMA = standardType === 'MA';
    const standardDisplayName = isMA ? 'Medicare Advantage (MA)' : 'Qualified Health Plan (QHP)';

    // URLs for regulatory standards - only show for QHP, not MA
    const faqUrl = isMA ? '' : 'https://www.qhpcertification.cms.gov/QHP/faqs/Network-Adequacy-FAQs';

    const standardsDocUrl = isMA ? '' : `https://www.qhpcertification.cms.gov/QHPvforcesite/apex/FileDownload?file=PY${year}-QHP-Issuer-Instructions-AppE-NA-v1-v1`;

    const {providerMaxTimeDist, facilityMaxTimeDist} = useMemo(() => {
        const providerMaxTimeDist: IMaxTimeDistTableRow[] = [];
        const facilityMaxTimeDist: IMaxTimeDistTableRow[] = [];
        maxTimeDistData.forEach(item => {
            if (item.provider_facility_type === 'Provider') {
                providerMaxTimeDist.push(item);
            } else if (item.provider_facility_type === 'Facility') {
                facilityMaxTimeDist.push(item);
            }
        });
        return {providerMaxTimeDist, facilityMaxTimeDist};
    }, [maxTimeDistData]);

    const getLabel = (value: string) => {
        return value || '0';
    };

    const getTableCellLabels = (row: IMaxTimeDistTableRow) => {
        return [
            row.specialty_type,
            getLabel(row.large_metro_time),
            getLabel(row.large_metro_distance),
            getLabel(row.metro_time),
            getLabel(row.metro_distance),
            getLabel(row.micro_time),
            getLabel(row.micro_distance),
            getLabel(row.rural_time),
            getLabel(row.rural_distance),
            getLabel(row.ceac_time),
            getLabel(row.ceac_distance),
        ];
    };

    return (
        <article>
            <PageWithFootnotes
                footnotes={faqUrl ? [{id: '1', text: faqUrl}] : []}
            >
                <h2 id='i-introduction'>I. Introduction</h2>
                <p>
                    This report evaluates network access and adequacy against geographic time and distance standards. In this report,
                    the {year} {standardDisplayName} standards are used as the criteria for measuring geographic network
                    adequacy. These standards are measured in terms of driving time (minutes) and driving distance (miles). The {standardType} standards
                    provide granularity and specificity based on the geographic classification of counties to account for
                    geographic diversity.
                </p>
                <section id="specialty-types" className="specialty-types">
                    <h3>SPECIALTY TYPES</h3>
                    <h4 className='asset-header'>[TABLE 1] {standardType} SPECIALTY GROUPINGS</h4>
                    <SpecialtyGroupingsTable
                        providerSpecialties={providerSpecialties}
                        facilitySpecialties={facilitySpecialties}
                        loading={specialtyGroupings.loading}
                        error={specialtyGroupings.error}
                    />
                </section>
                <section id="county-designation" className="county-designation">
                    <h3>COUNTY DESIGNATION</h3>
                    <p>
                        {standardType} standards classify counties into five county-type designations{standardType === 'QHP' && ` in alignment with Medicare Advantage standards`}.
                        The parameters for these designations are based on approaches used by the Census Bureau and the Office of
                        Management and Budget{faqUrl && <sup>1</sup>}. These standards vary based on the following categories:
                    </p>
                    <ul>
                        <li>Individual provider specialty types</li>
                        <li>Facility specialty types</li>
                        <li>County designations (Large Metro, Metro, Micro, Rural, Counties with Extreme Access Considerations (CEAC)).</li>
                    </ul>
                </section>
            </PageWithFootnotes>
            <PageBreak />
            <section id={`py${year}-${standardType.toLowerCase()}-network-access-standards`} className={`${standardType.toLowerCase()}-standards`}>
                <PageWithFootnotes
                    footnotes={standardsDocUrl ? [{id: '2', text: standardsDocUrl}] : []}
                >
                    <h3>PY{year} {standardType} NETWORK ACCESS STANDARDS</h3>
                    <h4 className='asset-header'>[TABLE 2] MAXIMUM TIME AND DISTANCE STANDARDS FOR PY{year} {standardType} CERTIFICATION<sup>2</sup></h4>
                    <ReportsTable
                        rows={providerMaxTimeDist}
                        headers={tableHeaders.providerStandardsTableHeaders}
                        getTableCellLabels={getTableCellLabels}
                        error={_maxTimeDistData.error}
                        loading={_maxTimeDistData.loading}
                        loadingRows={25}
                        loadingColumns={11}
                    />
                </PageWithFootnotes>
                <PageBreak />
                <ReportsTable
                    rows={facilityMaxTimeDist}
                    headers={tableHeaders.facilityStandardsTableHeaders}
                    getTableCellLabels={getTableCellLabels}
                    error={_maxTimeDistData.error}
                    loading={_maxTimeDistData.loading}
                    loadingRows={11}
                    loadingColumns={11}
                />
                <div>
                    <MapContainer
                        key="introduction"
                        id="introduction"
                        mapStyle={{
                            width: 451,
                            height: 385,
                            margin: '0 auto'
                        }}
                        title={`[FIGURE 1] ${states.join(' AND ').toUpperCase()} COUNTY DESIGNATIONS`}
                        showBoundaries
                        countyFillPaintFactory={createDesignationFillPaint}
                        legend={
                            <MapLegend
                                type="categorical"
                                title="Region"
                                items={Object.entries(DESIGNATION_COLORS).map(([label, color]) => ({color, label}))}
                            />
                        }
                    />
                </div>
            </section>
        </article>
    );
};

export default Introduction;
